import { map } from 'rxjs';
import { TtmTicket } from './../../classes/ttm-ticket.class';
import { Component, computed, OnInit, signal, WritableSignal } from '@angular/core';
import { CodaltComponent } from '../codalt/codalt.component';
import { TtmTicketService } from '../../services/ttm-ticket.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TtmStatus } from '../../classes/ttmstatus';
import { TtmService } from '../../services/ttm.service';
import { TtmType } from '../../classes/ttmtype';
import { FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { AuthorisationService } from '../../services/auth/authorisation.service';
import { LocalStorage } from '../../utils/storage';
import { Roles } from '../../services/auth/roles';
import { ConfirmDialogService } from '../../services/confirm-dialog-service/confirm-dialog.service';
import { User } from '../../classes/user';
import { TtmTicketStatus } from '../../classes/ttm-ticket-status';
import { ErrorStateMatcher } from '@angular/material/core';
import { TtmTicketDepartmentOptions } from '../ttm-ticket-dialog/ttm-ticket-dialog.component';
import { Routenames } from '../../routenames';
import { DrawerService } from '../generic/drawer/drawer.service';
import { WorkorderDetailComponent } from '../workorder-detail/workorder-detail.component';

@Component({
  selector: 'app-ttm-ticket-detail',
  templateUrl: './ttm-ticket-detail.component.html',
  styleUrl: './ttm-ticket-detail.component.scss'
})
export class TtmTicketDetailComponent extends CodaltComponent implements OnInit {
    ttmTicket: WritableSignal<TtmTicket | null> = signal<TtmTicket | null>(null);

    // Newest comment first, with the avatar initials and color worked out once per ticket load
    sortedComments = computed(() => [...(this.ttmTicket()?.comments ?? [])]
        .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
        .map(comment => ({...comment, initials: this.initials(comment.user?.name), avatarColor: (comment.user?.id ?? 0) % 6})));

    // Same rule as in the ticket overview: the reporter and the coordinator may delete a ticket
    canDeleteTicket = computed(() => this.ttmTicket()?.user_id === LocalStorage.user?.id || AuthorisationService.hasRole(Roles.Codalt));

    ttmStatuses: (TtmStatus & {textColor: string})[] = [];
    ttmTypes: TtmType[] = [];
    users: User[] = [];
    ticketStatuses: TtmTicketStatus[] = [];
    departmentOptions: { key: string; name: string }[] = Object.entries(TtmTicketDepartmentOptions).map(([key, name]) => ({key, name}));

    commentForm: FormGroup = new FormGroup({
        text: new FormControl('', [Validators.required, Validators.minLength(1)])
    });

    ticketForm: FormGroup = new FormGroup({
        comment: new FormControl('', [Validators.maxLength(1000)]),
        assignee_id: new FormControl(null),
        status: new FormControl(''),
        department: new FormControl<string | null>(null),
        azure_work_item_id: new FormControl<string | null>(null, [Validators.pattern(/^[1-9][0-9]*$/)])
    });

    // An empty comment only disables the add button, the field itself never turns red
    noErrorState: ErrorStateMatcher = {isErrorState: () => false};

    isEditingTicket = false;
    isEditingAssignee = false;

    currentUser = LocalStorage.user;

    constructor(
        private ttmTicketService: TtmTicketService,
        private ttmService: TtmService,
        private route: ActivatedRoute,
        private confirmDialogService: ConfirmDialogService,
        private router: Router,
        private drawerService: DrawerService
    ) {
        super();
    }

    ngOnInit() {
        const ticketId = this.route.snapshot.paramMap.get('ticketId');
        if (ticketId !== null) {
            this.subscriptions.add(
                this.ttmTicketService.getTicket(+ticketId).pipe(
                    map(response => response.data),
                ).subscribe(data => {
                    this.ttmTicket.set(data);
                })
            );

            const statuses$ = this.ttmService.statuses();
            const types$ = this.ttmService.types();

            this.subscriptions.add(
                statuses$.subscribe(response => {
                    this.ttmStatuses = response.data.map(status => ({...status, textColor: this.contrastTextColor(status.color)}));
                })
            );

            this.subscriptions.add(
                types$.subscribe(response => {
                    this.ttmTypes = response.data;
                })
            );

            // Load users for assignee dropdown
            this.subscriptions.add(
                this.ttmTicketService.getAssignees().subscribe(response => {
                    this.users = response.data;
                })
            );

            // Load ticket statuses
            this.subscriptions.add(
                this.ttmTicketService.getTicketStatuses().subscribe(response => {
                    this.ticketStatuses = response.data;
                })
            );
        } else {
            // Handle missing ticketId, e.g. set ticket to null or show an error
            this.ttmTicket.set(null);
        }
    }

    onSubmitComment(formDirective: FormGroupDirective) {
        if (this.commentForm.valid && this.ttmTicket()) {
            const commentText = this.commentForm.get('text')?.value;
            const ticketId = this.ttmTicket()?.id;

            if (ticketId && commentText) {
                this.subscriptions.add(
                    this.ttmTicketService.addComment(ticketId, commentText).subscribe({
                        next: (response) => {
                            // Refresh the ticket data to show the new comment
                            this.subscriptions.add(
                                this.ttmTicketService.getTicket(ticketId).pipe(
                                    map(ticketResponse => ticketResponse.data)
                                ).subscribe(ticket => {
                                    this.ttmTicket.set(ticket);
                                })
                            );
                            // resetForm() also clears the submitted state, reset() leaves the empty required field red
                            formDirective.resetForm();
                        },
                        error: (error) => {
                            console.error('Error adding comment:', error);
                        }
                    })
                );
            }
        }
    }

    deleteComment(comment: any) {
        this.confirmDialogService.confirm(
            'Opmerking verwijderen',
            `Weet je zeker dat je deze opmerking van ${comment.user?.name} wilt verwijderen?`,
            'Verwijderen',
            'Behouden'
        ).then(() => {
            const ticketId = this.ttmTicket()?.id;
            if (ticketId) {
                this.subscriptions.add(
                    this.ttmTicketService.deleteComment(ticketId, comment.id).subscribe({
                        next: () => {
                            // Refresh the ticket data to remove the deleted comment
                            this.subscriptions.add(
                                this.ttmTicketService.getTicket(ticketId).pipe(
                                    map(ticketResponse => ticketResponse.data)
                                ).subscribe(ticket => {
                                    this.ttmTicket.set(ticket);
                                })
                            );
                        },
                        error: (error) => {
                            console.error('Error deleting comment:', error);
                        }
                    })
                );
            }
        }, () => {
            // User canceled, do nothing
        });
    }

    startEditingTicket() {
        const ticket = this.ttmTicket();
        if (ticket) {
            this.ticketForm.patchValue({
                comment: ticket.comment || '',
                assignee_id: ticket.assignee_id || null,
                status: ticket.status || 'new',
                department: ticket.department ?? null,
                azure_work_item_id: ticket.azure_work_item_id?.toString() ?? null
            });
            this.isEditingTicket = true;
        }
    }

    cancelEditingTicket() {
        this.isEditingTicket = false;
        this.ticketForm.reset();
    }

    saveTicket() {
        if (this.ticketForm.valid && this.ttmTicket()) {
            const ticketId = this.ttmTicket()?.id;
            const commentText = this.ticketForm.get('comment')?.value;
            const assigneeId = this.ticketForm.get('assignee_id')?.value;
            const status = this.ticketForm.get('status')?.value;

            const payload: any = { comment: commentText, assignee_id: assigneeId, status: status };
            // Only the coordinator may change the department and the Azure DevOps link
            if (AuthorisationService.hasRole(Roles.Codalt)) {
                payload.department = this.ticketForm.get('department')?.value ?? null;
                payload.azure_work_item_id = this.ticketForm.get('azure_work_item_id')?.value || null;
            }

            if (ticketId !== undefined) {
                this.subscriptions.add(
                    this.ttmTicketService.updateTicket(ticketId, payload).subscribe({
                        next: (response) => {
                            // Refresh the ticket to get updated data
                            this.subscriptions.add(
                                this.ttmTicketService.getTicket(ticketId).pipe(
                                    map(ticketResponse => ticketResponse.data)
                                ).subscribe(ticket => {
                                    this.ttmTicket.set(ticket);
                                })
                            );
                            this.isEditingTicket = false;
                        },
                        error: (error) => {
                            console.error('Error updating ticket:', error);
                        }
                    })
                );
            }
        }
    }

    deleteTicket() {
        const ticketId = this.ttmTicket()?.id;
        this.confirmDialogService.confirm(
            'Verwijderen bevestigen',
            'Weet u zeker dat u dit ticket wilt verwijderen?',
            'Verwijderen',
            'Behouden'
        ).then(() => {
            this.subscriptions.add(
                this.ttmTicketService.deleteTicket(ticketId!).subscribe(() => {
                    this.router.navigate(['/', Routenames.TtmTickets]);
                })
            );
        }, () => {
        });
    }

    // Dark text on light status colors (like 00FF00), white text on dark ones
    private contrastTextColor(hex: string): string {
        const [r, g, b] = [0, 2, 4].map(i => parseInt(hex.substring(i, i + 2), 16));
        return (0.299 * r + 0.587 * g + 0.114 * b) > 160 ? '#111827' : '#fff';
    }

    // "M. Bakker" -> "MB"
    private initials(name?: string): string {
        const parts = (name ?? '').split(/[\s.]+/).filter(Boolean);
        if (!parts.length) {
            return '?';
        }
        return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
    }

    // Only toggles this ticket, the response is the new setting, so no refetch is needed
    toggleMuteNotifications() {
        const ticket = this.ttmTicket();
        if (!ticket?.id) return;

        const action$ = ticket.is_muted?.email_enabled
            ? this.ttmTicketService.muteNotifications(ticket.id)
            : this.ttmTicketService.unmuteNotifications(ticket.id);

        this.subscriptions.add(
            action$.subscribe(response => this.ttmTicket.set({...ticket, is_muted: response.data}))
        );
    }

    // Same drawer as on the workorders page, but on top of the ticket so the user stays here
    openWorkorder(workorderId: number) {
        this.drawerService.open(WorkorderDetailComponent, 'right', {workorderId, viaTicket: true});
    }
}
